// Statistics.js
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box, Grid, Typography, useTheme, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const statisticsData = [
  { label: "Students Enrolled", value: "50K+" },
  { label: "Years of Experience", value: "20+" },
  { label: "Various Skill Courses", value: "10+" },
  { label: "Awards Won", value: "25+" },
];

const colors = ["#e53935", "#43a047", "#1e88e5", "#fbc02d"];

const Statistics = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // true if screen is <600px

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const sliderSettings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 4000,
      arrows: false,
    }),
    []
  );

  const renderStatCard = (stat, index) => (
    <motion.div
      key={index}
      initial={{ opacity: 0, scale: 0.5 }}
      animate={isVisible ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, delay: index * 0.2 }}
    >
      <Box
        sx={{
          padding: 3,
          textAlign: "center",
          borderBottom: `4px solid ${colors[index % colors.length]}`,
          backgroundColor: "white",
          borderRadius: 1,
          boxShadow: 3,
        }}
      >
        <Typography variant="h3" color="primary">
          {stat.value}
        </Typography>
        <Typography variant="h6" color="textSecondary">
          {stat.label}
        </Typography>
      </Box>
    </motion.div>
  );

  return (
    <Box
      ref={sectionRef}
      sx={{ p: 4, backgroundColor: "#f5f5f5", borderRadius: 2 }}
    >
      <Typography
        variant="h4"
        gutterBottom
        align="center"
        sx={{
          fontWeight: "bold",
          color: "#333",
          mb: 4,
          textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
        }}
      >
        Our Achievements
      </Typography>

      {isMobile ? (
        <Box>
          <Slider {...sliderSettings}>
            {statisticsData.map((stat, index) => (
              <Box key={index} px={2}>
                {renderStatCard(stat, index)}
              </Box>
            ))}
          </Slider>
        </Box>
      ) : (
        <Grid container spacing={4} justifyContent="center">
          {statisticsData.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              {renderStatCard(stat, index)}
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default Statistics;
